import type { NFT } from "@stepfinance/nft";
import FsLightbox from "fslightbox-react";
import React, { useEffect, useMemo, useState } from "react";

import { stringIsImageData } from "../../constants/regex";
import { isStarAtlasNFT } from "../../models/nfts/nfts";
import { StarAtlasInstructionModal, StarAtlasToolbarButtons } from "../dashboard/NFTGrid/StarAtlasComponents";

interface NFTLightboxProps {
  focusedNFTRef: React.RefObject<HTMLButtonElement | HTMLImageElement>;
  viewingNFT: NFT;
}

export const NFTLightbox: React.FC<NFTLightboxProps> = ({ focusedNFTRef, viewingNFT }) => {
  const [audio, setAudio] = useState<HTMLAudioElement | undefined>();
  const [audioPlaying, setAudioPlaying] = useState(false);

  const [toggler, setToggler] = useState(false);
  const [key, setKey] = useState<number>(0); // necessary for FsLightbox props changes, see https://fslightbox.com/react/documentation/updating-props

  const [showStarAtlasInstructionModal, setShowStarAtlasInstructionModal] = useState(false);
  const [showStarAtlasToolBarButtons, setShowStarAtlasToolBarButtons] = useState(false);

  useEffect(() => {
    setToggler(!toggler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewingNFT]);

  const lightboxImage = useMemo(() => {
    const imagePath = viewingNFT.json?.image;
    let newURL: string | JSX.Element = process.env.PUBLIC_URL + imagePath;
    if (imagePath?.startsWith("http")) {
      newURL = imagePath;
    } else if (imagePath && stringIsImageData.test(imagePath)) {
      newURL = <img src={imagePath.replace(stringIsImageData, "")} alt={viewingNFT?.name} />;
    }
    if (viewingNFT.json?.animation_url) {
      return [viewingNFT.json.animation_url];
    } else {
      return [newURL];
    }
  }, [viewingNFT.json?.animation_url, viewingNFT.json?.image, viewingNFT?.name]);

  useEffect(() => {
    if (isStarAtlasNFT(viewingNFT)) {
      const audioToPlay = viewingNFT.json.media.audio;
      if (audioToPlay) {
        setAudio(new Audio(audioToPlay));
        setShowStarAtlasToolBarButtons(true);
      }
    }
  }, [viewingNFT]);

  const lightboxEvents = {
    onOpen: () => {
      audio?.load();
      focusedNFTRef.current?.focus();
      focusedNFTRef.current?.scrollIntoView({
        block: "center",
        behavior: "smooth",
      });
    },
    onClose: () => {
      audio?.pause();
      setAudio(undefined);
      setKey(key + 1);
      focusedNFTRef.current?.scrollIntoView({
        block: "center",
      });
      focusedNFTRef.current?.blur();
      setShowStarAtlasInstructionModal(false);
      setShowStarAtlasToolBarButtons(false);
    },
  };

  const starAtlasClickEvents = {
    toolbarButtons: {
      instructionsButtonClickEvent: () => {
        setShowStarAtlasInstructionModal(!showStarAtlasInstructionModal);
      },
      soundButtonClickEvent: () => setAudioPlaying(!audioPlaying),
    },
    instructionModalClickEvent: () => {
      setShowStarAtlasInstructionModal(false);
      void audio?.play();
    },
  };

  useEffect(() => {
    if (audioPlaying) {
      audio?.pause();
    } else {
      void audio?.play();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioPlaying]);

  const isVideo = lightboxImage.length && typeof lightboxImage[0] === "string" && !!lightboxImage[0].match(/mp4$/gim);

  return (
    <>
      <FsLightbox
        disableLocalStorage={true}
        key={key}
        loadOnlyCurrentSource={true}
        onOpen={lightboxEvents.onOpen}
        onClose={lightboxEvents.onClose}
        sources={lightboxImage}
        toggler={toggler}
        disableThumbs={true}
        type={isVideo ? "video" : "image"}
        customAttributes={isVideo ? [{ autoplay: "true" }] : []}
      />
      {isStarAtlasNFT(viewingNFT) && showStarAtlasToolBarButtons ? (
        <StarAtlasToolbarButtons
          instructionsButtonClickEvent={starAtlasClickEvents.toolbarButtons.instructionsButtonClickEvent}
          soundButtonClickEvent={starAtlasClickEvents.toolbarButtons.soundButtonClickEvent}
          soundPlaying={audioPlaying}
        />
      ) : null}
      {isStarAtlasNFT(viewingNFT) && showStarAtlasInstructionModal ? (
        <StarAtlasInstructionModal
          qrFacebook={viewingNFT.json.media.qrFacebook ?? ""}
          qrInstagram={viewingNFT.json.media.qrInstagram ?? ""}
          onClickEvent={starAtlasClickEvents.instructionModalClickEvent}
        />
      ) : null}
    </>
  );
};
